import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import FloatingActionButton from "./FloatingActionButton";
import { PhoneIcon, XIcon } from "../icons";

const FloatingDialPad = ({
  onCall,
  position = "bottom-right",
  glassMorphism = true,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isHovered, setIsHovered] = useState(null);

  const dialPadNumbers = [
    { number: "1", letters: "" },
    { number: "2", letters: "ABC" },
    { number: "3", letters: "DEF" },
    { number: "4", letters: "GHI" },
    { number: "5", letters: "JKL" },
    { number: "6", letters: "MNO" },
    { number: "7", letters: "PQRS" },
    { number: "8", letters: "TUV" },
    { number: "9", letters: "WXYZ" },
    { number: "*", letters: "" },
    { number: "0", letters: "+" },
    { number: "#", letters: "" },
  ];

  const handleNumberClick = (number) => {
    setPhoneNumber((prev) => prev + number);
    // Haptic feedback simulation
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }
  };

  const handleCall = () => {
    if (phoneNumber && onCall) {
      onCall(phoneNumber);
      setPhoneNumber("");
      setIsOpen(false);
    }
  };

  const handleBackspace = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPhoneNumber("");
  };

  const formatPhoneNumber = (number) => {
    // Format as (XXX) XXX-XXXX for US numbers
    const cleaned = number.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return number;
  };

  const glassStyles =
    "bg-surface-container border border-outline-variant shadow-sm";

  return (
    <>
      {/* Main FAB */}
      <FloatingActionButton
        icon={isOpen ? XIcon : PhoneIcon}
        onClick={() => setIsOpen(!isOpen)}
        position={position}
        color="primary"
        size="large"
        tooltip={isOpen ? "Close dial pad" : "Open dial pad"}
        disabled={disabled}
        glassMorphism={glassMorphism}
      />

      {/* Dial Pad Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-end justify-center sm:items-center p-4 bg-black/50"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 100 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 100 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className={`
                w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden
                ${glassStyles}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Phone Number Display */}
              <div className="p-6 text-center">
                <div className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                  Phone Number
                </div>
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="tel"
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, ""))
                    }
                    className={`
                      text-2xl font-semibold text-center bg-transparent
                      border-0 outline-none w-full
                      text-gray-900 dark:text-white
                      placeholder-gray-400 dark:placeholder-gray-500
                    `}
                    placeholder="Enter number"
                  />
                  {phoneNumber && (
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={handleBackspace}
                      className="p-2 rounded-lg hover:bg-surface-container-high transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
                        />
                      </svg>
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Dial Pad Grid */}
              <div className="grid grid-cols-3 gap-2 p-4">
                {dialPadNumbers.map((item) => (
                  <motion.button
                    key={item.number}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => setIsHovered(item.number)}
                    onMouseLeave={() => setIsHovered(null)}
                    onClick={() => handleNumberClick(item.number)}
                    className={`
                      relative p-4 rounded-xl transition-all duration-200
                      bg-surface-container-high hover:bg-surface-container-highest
                      ${isHovered === item.number ? "shadow-md" : ""}
                    `}
                  >
                    {/* Ripple Effect Background */}
                    <AnimatePresence>
                      {isHovered === item.number && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1.5, opacity: 0.3 }}
                          exit={{ scale: 2, opacity: 0 }}
                          className="absolute inset-0 rounded-xl bg-blue-400"
                        />
                      )}
                    </AnimatePresence>

                    <div className="relative z-10">
                      <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {item.number}
                      </div>
                      {item.letters && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {item.letters}
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 p-4 pt-0">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleClear}
                  className="flex-1 py-3 px-4 rounded-xl font-medium transition-all bg-red-100 hover:bg-red-200 text-red-600"
                  disabled={!phoneNumber}
                >
                  Clear
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCall}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    phoneNumber
                      ? "bg-green-900/40 hover:bg-green-800/60 text-green-400"
                      : "bg-surface-container-high text-on-surface-variant cursor-not-allowed"
                  }`}
                  disabled={!phoneNumber}
                >
                  <PhoneIcon className="w-5 h-5" />
                  Call
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

FloatingDialPad.propTypes = {
  onCall: PropTypes.func.isRequired,
  position: PropTypes.oneOf([
    "bottom-right",
    "bottom-left",
    "top-right",
    "top-left",
  ]),
  glassMorphism: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default FloatingDialPad;
